import { useState, useEffect, useRef, useCallback } from 'react';
import { transactionTracker, TransactionStatus, TransactionTrackingResult, TransactionTrackingCallbacks } from '../services/transaction/transactionTracker';

export interface UseTransactionTrackerOptions {
  /**
   * Whether to start tracking immediately when the hook is called
   */
  autoStart?: boolean;
  
  /**
   * Custom polling interval in milliseconds (default: 4000)
   */
  pollingInterval?: number;
  
  /**
   * Callbacks for different transaction states
   */
  callbacks?: TransactionTrackingCallbacks;
}

export interface UseTransactionTrackerResult {
  /**
   * Current transaction status
   */
  status: TransactionStatus | null;
  
  /**
   * Current status message
   */
  message: string | null;
  
  /**
   * Transaction data
   */
  data: any;
  
  /**
   * Whether the transaction is currently being tracked
   */
  isTracking: boolean;
  
  /**
   * Whether the transaction is in a loading state (pending, surge_pricing)
   */
  isLoading: boolean;
  
  /**
   * Whether the transaction completed successfully
   */
  isCompleted: boolean;
  
  /**
   * Whether there was an error
   */
  isError: boolean;
  
  /**
   * Error message if any
   */
  error: string | null;
  
  /**
   * Start tracking the transaction
   */
  startTracking: (transactionId: string) => Promise<void>;
  
  /**
   * Stop tracking the current transaction
   */
  stopTracking: () => void;
  
  /**
   * Get transaction status once (without continuous tracking)
   */
  getStatus: (transactionId: string) => Promise<TransactionTrackingResult>;
  
  /**
   * Reset the hook state
   */
  reset: () => void;
}

export const useTransactionTracker = (
  initialTransactionId?: string,
  options: UseTransactionTrackerOptions = {}
): UseTransactionTrackerResult => {
  const [status, setStatus] = useState<TransactionStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentTransactionIdRef = useRef<string | null>(null);
  const { autoStart = false, pollingInterval = 4000, callbacks = {} } = options;

  // Set polling interval if provided
  useEffect(() => {
    if (pollingInterval) {
      transactionTracker.setPollingInterval(pollingInterval);
    }
  }, [pollingInterval]);

  // Derived states
  const isLoading = status === 'pending' || status === 'surge_pricing';
  const isCompleted = status === 'completed';
  const isError = status === 'error';

  const reset = useCallback(() => {
    setStatus(null);
    setMessage(null);
    setData(null);
    setIsTracking(false);
    setError(null);
    currentTransactionIdRef.current = null;
  }, []);

  const stopTracking = useCallback(() => {
    if (currentTransactionIdRef.current) {
      transactionTracker.stopTracking(currentTransactionIdRef.current);
      setIsTracking(false);
    }
  }, []);

  const startTracking = useCallback(async (transactionId: string) => {
    // Stop any existing tracking
    if (currentTransactionIdRef.current) {
      transactionTracker.stopTracking(currentTransactionIdRef.current);
    }

    // Reset state
    reset();
    
    currentTransactionIdRef.current = transactionId;
    setIsTracking(true);
    setError(null);

    // Enhanced callbacks that update local state
    const enhancedCallbacks: TransactionTrackingCallbacks = {
      onStatusChange: (newStatus, newMessage) => {
        setStatus(newStatus);
        setMessage(newMessage || null);
        callbacks.onStatusChange?.(newStatus, newMessage);
      },
      
      onSurgePricing: () => {
        callbacks.onSurgePricing?.();
      },
      
      onProcessing: () => {
        callbacks.onProcessing?.();
      },
      
      onCompleted: (resultData) => {
        setStatus('completed');
        setData(resultData);
        setIsTracking(false);
        callbacks.onCompleted?.(resultData);
      },
      
      onError: (errorMessage) => {
        setStatus('error');
        setError(errorMessage);
        setIsTracking(false);
        callbacks.onError?.(errorMessage);
      }
    };

    try {
      const result = await transactionTracker.trackTransaction(transactionId, enhancedCallbacks);
      
      // Final state update
      setStatus(result.status);
      setMessage(result.message || null);
      setData(result.data);
      setIsTracking(false);
      
      if (result.status === 'error') {
        setError(result.message || 'Unknown error');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Tracking failed';
      setStatus('error');
      setError(errorMessage);
      setIsTracking(false);
    }
  }, [callbacks, reset]);

  const getStatus = useCallback(async (transactionId: string): Promise<TransactionTrackingResult> => {
    return transactionTracker.getTransactionStatus(transactionId);
  }, []);

  // Auto-start tracking if initialTransactionId is provided
  useEffect(() => {
    if (autoStart && initialTransactionId) {
      startTracking(initialTransactionId);
    }
  }, [autoStart, initialTransactionId, startTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentTransactionIdRef.current) {
        transactionTracker.stopTracking(currentTransactionIdRef.current);
      }
    };
  }, []);

  return {
    status,
    message,
    data,
    isTracking,
    isLoading,
    isCompleted,
    isError,
    error,
    startTracking,
    stopTracking,
    getStatus,
    reset
  };
};